import type { LiveClient, LiveClientConfig } from '@/transport/live/LiveClient';

export const DEFAULT_OPEN_TIMEOUT_MS = 5_000;
export const DEFAULT_MAX_RECONNECT_ATTEMPTS = 3;
export const DEFAULT_BASE_RECONNECT_DELAY_MS = 1_000;
export const DEFAULT_ACK_TIMEOUT_MS = 10_000;

export interface PendingAck {
  resolve: () => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Shared state and behaviour between `WsLiveClient` and `SocketIoLiveClient`.
 * Owns: config parsing, the single-slot pendingAck, cursor/click callbacks,
 * and the disposed flag. Each subclass owns its socket type and wire protocol.
 */
export abstract class BaseLiveClient implements LiveClient {
  abstract readonly name: string;

  protected disposed = false;
  protected pendingAck: PendingAck | null = null;
  protected cursorCallback: ((x: number, y: number) => void) | null = null;
  protected clickCallback: ((x: number, y: number) => void) | null = null;
  protected focusCallback: ((x: number, y: number) => void) | null = null;
  protected messageCallback: ((msg: { type: string; [k: string]: unknown }) => void) | null = null;

  protected readonly url: string;
  protected readonly sessionId: string;
  protected readonly apiKey: string;
  protected readonly openTimeoutMs: number;
  protected readonly maxReconnectAttempts: number;
  protected readonly baseReconnectDelayMs: number;
  protected readonly ackTimeoutMs: number;

  constructor(config: LiveClientConfig) {
    this.url = config.apiUrl;
    this.sessionId = config.sessionId;
    this.apiKey = config.apiKey;
    this.openTimeoutMs = config.openTimeoutMs ?? DEFAULT_OPEN_TIMEOUT_MS;
    this.maxReconnectAttempts = config.reconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    this.baseReconnectDelayMs = config.reconnectBaseDelayMs ?? DEFAULT_BASE_RECONNECT_DELAY_MS;
    this.ackTimeoutMs = config.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
  }

  onCursorMessage(cb: (x: number, y: number) => void): void {
    this.cursorCallback = cb;
  }

  onClickMessage(cb: (x: number, y: number) => void): void {
    this.clickCallback = cb;
  }

  onFocusMessage(cb: (x: number, y: number) => void): void {
    this.focusCallback = cb;
  }

  onMessage(cb: (msg: { type: string; [k: string]: unknown }) => void): void {
    this.messageCallback = cb;
  }

  abstract isReady(): boolean;
  abstract send(events: unknown[]): Promise<void>;
  abstract sendRaw(msg: Record<string, unknown>): void;
  abstract connect(): Promise<void>;
  abstract dispose(): void;

  protected resolvePendingAck(): void {
    const pending = this.pendingAck;
    if (!pending) return;
    this.pendingAck = null;
    clearTimeout(pending.timer);
    pending.resolve();
  }

  protected rejectPendingAck(err: Error): void {
    const pending = this.pendingAck;
    if (!pending) return;
    this.pendingAck = null;
    clearTimeout(pending.timer);
    pending.reject(err);
  }
}
